// Benchmark route: eSIM.io price comparison (primary product-team request).
// Per rival plan we show the comparable unit ($/GB for fixed, $/day for
// unlimited), the matched eSIM.io equivalent, and the delta %. A region filter
// lets the reader narrow to a country/market. No data is invented: rows with no
// eSIM.io equivalent are shown explicitly as "no baseline".

import React, { useEffect, useMemo, useState } from 'react';
import { t } from '../lib/i18n';
import type { Lang } from '../lib/i18n';
import { api } from '../lib/api';
import type { BenchmarkData, BenchmarkRow } from '../lib/api';
import { formatUSD, formatGB, formatDays } from '../lib/format';
import { formatWeekRange } from '../components/Header';

interface BenchmarkProps {
  lang: Lang;
  currentWeek: number | null;
  code: string;
}

function unitLabel(row: BenchmarkRow, lang: Lang): string {
  return row.mode === 'per_gb' ? t('perGb', lang) : t('perDay', lang);
}

function fmtUnit(v: number | null): string {
  if (v == null) return '–';
  return `$${v.toFixed(2)}`;
}

// Delta cell: negative = cheaper than eSIM.io (green), positive = pricier (red).
function DeltaCell({ row, lang }: { row: BenchmarkRow; lang: Lang }) {
  if (row.status === 'no_baseline') {
    return <span className="text-xs text-neutral-400">{t('noBaseline', lang)}</span>;
  }
  if (row.status === 'not_comparable' || row.delta_pct == null) {
    return <span className="text-xs text-neutral-400">–</span>;
  }
  const d = row.delta_pct;
  const cheaper = d < 0;
  const cls = cheaper ? 'text-emerald-700 bg-emerald-50 border-emerald-200' : 'text-rose-700 bg-rose-50 border-rose-200';
  const sign = d > 0 ? '+' : '';
  const phrase = cheaper ? t('cheaperThanEsimio', lang) : t('pricierThanEsimio', lang);
  return (
    <span className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-xs font-medium ${cls}`}>
      {sign}{d.toFixed(1)}%
      <span className="text-[10px] font-normal opacity-70 hidden sm:inline">{phrase}</span>
    </span>
  );
}

export function Benchmark({ lang, currentWeek, code }: BenchmarkProps) {
  const [data, setData] = useState<BenchmarkData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [region, setRegion] = useState<string>('all');

  useEffect(() => {
    if (!currentWeek) return;
    setLoading(true);
    setError(null);
    api.benchmark
      .byWeek(currentWeek, 'all')
      .then(setData)
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [currentWeek]);

  // Region options derived from the returned rows (comparable ones first).
  const regionOptions = useMemo(() => {
    if (!data) return [];
    const set = new Set<string>();
    for (const r of data.rows) if (r.region) set.add(r.region);
    return Array.from(set).sort();
  }, [data]);

  const rows = useMemo(() => {
    if (!data) return [];
    if (region === 'all') return data.rows;
    return data.rows.filter((r) => r.region === region);
  }, [data, region]);

  if (!currentWeek || loading) {
    return <div className="flex items-center justify-center py-24 text-neutral-500 text-sm">{t('loading', lang)}</div>;
  }
  if (error) {
    return <div className="flex items-center justify-center py-24 text-neutral-500 text-sm">{error}</div>;
  }

  const usedWeek = data?.used_week ?? currentWeek;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-6">
      {/* Header row */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-xs font-medium text-neutral-500 uppercase tracking-wider">
          {t('benchmarkTitle', lang)}
        </h1>
        <span className="text-xs text-neutral-500 tabular-nums">
          {t('weekLabel', lang)} {usedWeek % 100}
          <span className="text-neutral-300"> · {formatWeekRange(usedWeek)}</span>
        </span>
      </div>

      {/* Intro / methodology */}
      <div className="card p-4">
        <p className="text-xs text-neutral-600 leading-relaxed">{t('benchmarkIntro', lang)}</p>
        {data?.fell_back && (
          <p className="mt-2 text-xs text-amber-700 border-l-2 border-amber-300 pl-2 leading-relaxed">
            {t('benchmarkFallbackNote', lang).replace('{week}', String(usedWeek % 100))}
          </p>
        )}
      </div>

      {/* Region filter */}
      {regionOptions.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-neutral-500">{t('colCountry', lang)}:</span>
          <select
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            className="text-xs border border-neutral-300 rounded px-2 py-1 bg-white text-neutral-800"
          >
            <option value="all">{t('allRegions', lang)}</option>
            {regionOptions.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </div>
      )}

      {/* Table */}
      {rows.length === 0 ? (
        <div className="card p-8 text-center text-neutral-500 text-sm">{t('benchmarkNoData', lang)}</div>
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-neutral-200 text-neutral-500 text-left">
                <th className="px-3 py-2 font-medium">{t('colProvider', lang)}</th>
                <th className="px-3 py-2 font-medium">{t('colCountry', lang)}</th>
                <th className="px-3 py-2 font-medium">{t('colData', lang)}</th>
                <th className="px-3 py-2 font-medium">{t('colDays', lang)}</th>
                <th className="px-3 py-2 font-medium text-right">{t('colRivalPrice', lang)}</th>
                <th className="px-3 py-2 font-medium text-right">{t('colRivalUnit', lang)}</th>
                <th className="px-3 py-2 font-medium text-right">{t('colEsimioPrice', lang)}</th>
                <th className="px-3 py-2 font-medium text-right">{t('colEsimioUnit', lang)}</th>
                <th className="px-3 py-2 font-medium">{t('colDiff', lang)}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-b border-neutral-100 hover:bg-neutral-50">
                  <td className="px-3 py-2 text-neutral-800">
                    <a
                      href={`/providers/${encodeURIComponent(r.provider_id)}?code=${code}`}
                      className="hover:text-emerald-600 underline decoration-dotted decoration-neutral-300"
                    >
                      {r.provider_name}
                    </a>
                  </td>
                  <td className="px-3 py-2 text-neutral-600">{r.region ?? '–'}</td>
                  <td className="px-3 py-2 text-neutral-600">
                    {r.data_unlimited === 1 ? t('unlimited', lang) : formatGB(r.data_gb)}
                  </td>
                  <td className="px-3 py-2 text-neutral-600">{formatDays(r.validity_days)}</td>
                  <td className="px-3 py-2 text-right text-neutral-800 tabular-nums">{formatUSD(r.rival_price_usd)}</td>
                  <td className="px-3 py-2 text-right text-neutral-600 tabular-nums">
                    {fmtUnit(r.rival_unit)}<span className="text-neutral-400 ml-0.5">{unitLabel(r, lang)}</span>
                  </td>
                  <td className="px-3 py-2 text-right text-neutral-600 tabular-nums">
                    {r.esimio_price_usd != null ? formatUSD(r.esimio_price_usd) : '–'}
                  </td>
                  <td className="px-3 py-2 text-right text-neutral-600 tabular-nums">
                    {fmtUnit(r.esimio_unit)}{r.esimio_unit != null && <span className="text-neutral-400 ml-0.5">{unitLabel(r, lang)}</span>}
                  </td>
                  <td className="px-3 py-2"><DeltaCell row={r} lang={lang} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
